import { axiosInstance } from './base'
import config from '../../config.json'
import { objectToFormData } from '@/utils/object'
import queryString from 'query-string'

/**
 * @typedef {{id: number, grade: boolean, history: boolean, classLevel: number}} TrialExamPostData
 * @typedef {{questions: Array, testSettings: {Test: {id: number}}}} TestSessionData
 */

/**
 * Fetches an example question for the given lesson ID.
 *
 * @param {number} lessonId
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getExampleQuestion = lessonId => {
  return axiosInstance
    .get(`${config.apiUrl}/lessons/${lessonId}/questions/example`)
    .then(({ data }) => data)
}

/**
 * Fetches the result data for the given test session ID.
 * @param testId
 * @return {Promise<AxiosResponse<any>>}
 */
export const getTestResult = testId => {
  return axiosInstance
    .get(`${config.studentSiteUrl}/test/xhr_result/${testId}`)
    .then(({ data }) => data)
}

/**
 * Starts a Five Sharp test session and returns an array of questions as well as meta data for the test.
 *
 * @param {number} lessonId
 * @param {number} classLevel
 * @returns {Promise<AxiosResponse<TestSessionData>>}
 */
export const startFiveSharp = (lessonId, classLevel) => {
  return axiosInstance
    .post(
      `${config.apiUrl}/tests/startFiveSharpTest`,
      objectToFormData({
        class: classLevel,
        lesson_id: lessonId,
      })
    )
    .then(({ data }) => data)
}

/**
 * Starts a Trial Exam session and returns an array of questions as well as meta data for the test.
 *
 * @param {TrialExamPostData} postData
 * @returns {Promise<AxiosResponse<TestSessionData>>}
 */
export const startTrialExam = postData => {
  return axiosInstance
    .post(`${config.apiUrl}/tests/startTrialExam`, objectToFormData(postData))
    .then(({ data }) => data)
}

/**
 * Saves the given answer for the given question/test combination.
 *
 * @param {number} questionId
 * @param {number|string|null}answer
 * @param {number} lapsedTime
 * @param {number} testId
 * @param {UserAnswerModel} userAnswerModel
 * @returns {Promise}
 */
export const saveAnswer = (
  questionId,
  answer,
  lapsedTime,
  testId,
  userAnswerModel
) => {
  const query = {
    'data[question_id]': questionId,
    'data[answer]': answer,
    'data[lapsedTime]': lapsedTime,
    'data[test_id]': testId,
    'data[model]': userAnswerModel,
  }

  return axiosInstance
    .post(
      `${config.studentSiteUrl}/test/save_answer`,
      queryString.stringify(query)
    )
    .then(({ data }) => {
      return {
        ...data['question'],
        ...data['saved'],
      }
    })
}
